import type React from "react";
import { type CSSProperties, createContext, memo, useContext, useMemo, useState } from "react";
import { defaultLogoUrl } from "../../helpers/defaultUrls";
import {
  DEFAULT_FORM_BANNER_BACKGROUND_URL,
  DEFAULT_FORM_FULL_BACKGROUND_URL,
  DEFAULT_FORM_LAYOUT,
  getBackgroundImageCss,
  getBackgroundPosition,
  getBannerImageStyle,
  getBrightnessValue,
  getEffectiveHeaderEnabled,
  getFormBackgroundColorCss,
  getFormButtonColorCss,
  getFormFooterBackgroundColorCss,
  getFormFooterEnabled,
  getFormHeaderLogoHeightPx,
  getFormHeaderLogoWidthPx,
  getFormLayout,
  getFullImageStyle,
  getSolutionColorStyles,
  isEnterpriseSolutionColor,
} from "../../helpers/formStyle";

import "./PageContentBox.sass";
import type { FeatureFlags, FormState } from "../../types/form";
import { $t } from "../../utils/intl";
import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary";
import { FooterBranding } from "../FooterBranding/FooterBranding";
import { FormFooter } from "../FormFooter/FormFooter";
import { PageContentBoxText } from "./PageContentBox.text";
import { getLogoHref, isEmbeddedMode, shouldHideFormLogo } from "./PageContentBox.utils";

const ProgressBarSlotContext = createContext<HTMLDivElement | null>(null);
export const useProgressBarSlot = (): HTMLDivElement | null => useContext(ProgressBarSlotContext);

export interface PageContentBoxProps {
  formState?: FormState | null;
  featureFlags?: FeatureFlags;
  isPaidPlan?: boolean;
  logoUrl?: string | null;
  bannerImageUrl?: string | null;
  fullImageUrl?: string | null;
  displayBranding?: boolean;
  useEnterprisePalette?: boolean;
  solutionColor?: string | null;
  children: React.ReactNode;
}

interface FormHeaderLogoProps {
  logoUrl: string;
  logoLabel: string;
  logoStyle: CSSProperties;
  linkUrl?: string;
}

const FormHeaderLogo: React.FC<FormHeaderLogoProps> = ({
  logoUrl,
  logoLabel,
  logoStyle,
  linkUrl,
}) => {
  const logo = (
    <img
      src={logoUrl}
      data-testid="logo"
      alt={logoLabel}
      className="page-content-box__form-header-logo"
      style={logoStyle}
    />
  );

  return (
    <div className="page-content-box__form-header">
      {linkUrl ? (
        <a
          href={linkUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={logoLabel}
          className="page-content-box__form-header-link"
        >
          {logo}
        </a>
      ) : (
        logo
      )}
    </div>
  );
};

const PageContentBoxImpl: React.FC<PageContentBoxProps> = ({
  formState,
  featureFlags,
  isPaidPlan,
  logoUrl,
  bannerImageUrl,
  fullImageUrl,
  displayBranding = false,
  useEnterprisePalette,
  solutionColor,
  children,
}) => {
  // The palette follows the solution color family: enterprise solution colors
  // resolve form-style tokens against the enterprise palette, legacy solution
  // colors against the old base palette. The prop remains as an explicit
  // override for tests.
  const enterprisePalette = useEnterprisePalette ?? isEnterpriseSolutionColor(solutionColor);
  const headerStyleEnabled = featureFlags?.form_header_style === true;
  const appearanceStyleEnabled = featureFlags?.form_appearance_style === true;
  const footerStyleEnabled = featureFlags?.form_footer_style === true;
  const isPopup = isEmbeddedMode();
  const layoutStyleEnabled = featureFlags?.form_layout_style === true && !isPopup;

  const layout = layoutStyleEnabled ? getFormLayout(formState?.style) : DEFAULT_FORM_LAYOUT;
  const bannerImage = getBannerImageStyle(layoutStyleEnabled ? formState : null);
  const fullImage = getFullImageStyle(layoutStyleEnabled ? formState : null);
  const headerEnabled =
    (headerStyleEnabled
      ? getEffectiveHeaderEnabled(formState)
      : formState != null && formState.logo_handle !== "") && !shouldHideFormLogo();

  const isDefaultLogo = defaultLogoUrl === logoUrl;
  const linkUrl = getLogoHref(isDefaultLogo, isPaidPlan, formState?.logo_href);
  const logoLabel = $t(PageContentBoxText.formHeaderLogoAlt);

  const buttonColor = appearanceStyleEnabled ? formState?.style?.button_color : undefined;
  const backgroundColor = appearanceStyleEnabled ? formState?.style?.background_color : undefined;
  const headerBackgroundColor = headerStyleEnabled
    ? formState?.style?.header_background_color
    : undefined;
  const headerLogoSize = headerStyleEnabled ? formState?.style?.header_logo_size : undefined;

  const sectionVars = useMemo<CSSProperties>(() => {
    const vars: Record<string, string> = {
      "--form-button-color": getFormButtonColorCss(buttonColor, {
        useEnterprisePalette: enterprisePalette,
      }),
    };

    if (headerStyleEnabled && headerEnabled) {
      vars["--form-header-bg-color"] = getFormBackgroundColorCss(headerBackgroundColor, {
        useEnterprisePalette: enterprisePalette,
        layout: "classic",
      });
    }

    if (layout === "classic" || layout === "banner") {
      vars["--form-bg-color"] = getFormBackgroundColorCss(backgroundColor, {
        useEnterprisePalette: enterprisePalette,
        layout,
      });
    }

    if (footerStyleEnabled && getFormFooterEnabled(formState?.style)) {
      vars["--form-footer-bg-color"] = getFormFooterBackgroundColorCss(
        formState?.style?.footer_background_color,
      );
    }

    return { ...vars, ...getSolutionColorStyles(solutionColor) } as CSSProperties;
  }, [
    buttonColor,
    backgroundColor,
    headerBackgroundColor,
    headerEnabled,
    headerStyleEnabled,
    footerStyleEnabled,
    formState?.style,
    layout,
    enterprisePalette,
    solutionColor,
  ]);

  const fullBgUrl = fullImageUrl ?? DEFAULT_FORM_FULL_BACKGROUND_URL;
  const bannerBgUrl = bannerImageUrl ?? DEFAULT_FORM_BANNER_BACKGROUND_URL;
  const fullBrightness = getBrightnessValue(fullImage);
  const fullPosition = fullImage.position;
  const bannerBrightness = getBrightnessValue(bannerImage);
  const bannerPosition = bannerImage.position;

  const fullImageStyle = useMemo<CSSProperties | undefined>(() => {
    if (layout !== "full") return undefined;
    return {
      backgroundImage: getBackgroundImageCss(fullBgUrl, fullBrightness),
      backgroundPosition: getBackgroundPosition(fullPosition),
    };
  }, [layout, fullBgUrl, fullBrightness, fullPosition]);

  const bannerImageStyle = useMemo<CSSProperties | undefined>(() => {
    if (layout !== "banner") return undefined;
    return {
      backgroundImage: getBackgroundImageCss(bannerBgUrl, bannerBrightness),
      backgroundPosition: getBackgroundPosition(bannerPosition),
    };
  }, [layout, bannerBgUrl, bannerBrightness, bannerPosition]);

  const logoStyle = useMemo<CSSProperties>(
    () => ({
      maxWidth: getFormHeaderLogoWidthPx(headerLogoSize),
      maxHeight: getFormHeaderLogoHeightPx(headerLogoSize),
    }),
    [headerLogoSize],
  );

  const showBgColor = !isPopup && (layout === "classic" || layout === "banner");

  const [progressBarSlot, setProgressBarSlot] = useState<HTMLDivElement | null>(null);

  return (
    <ProgressBarSlotContext.Provider value={progressBarSlot}>
      <div className="page-content-box-root" style={sectionVars}>
        {fullImageStyle && (
          <>
            <link rel="preload" as="image" href={fullBgUrl} />
            <div
              aria-hidden="true"
              className="page-content-box__background"
              style={fullImageStyle}
            />
          </>
        )}
        {showBgColor && <div aria-hidden="true" className="page-content-box__color-backdrop" />}
        {bannerImageStyle && (
          <>
            <link rel="preload" as="image" href={bannerBgUrl} />
            <div aria-hidden="true" className="page-content-box__banner" style={bannerImageStyle} />
          </>
        )}
        <section className={`page-content-box page-content-box--${layout}`}>
          <div ref={setProgressBarSlot} className="page-content-box__progress-bar-slot" />
          <div className="page-content-box__card">
            {headerEnabled && logoUrl && (
              <FormHeaderLogo
                logoUrl={logoUrl}
                logoLabel={logoLabel}
                logoStyle={logoStyle}
                linkUrl={linkUrl}
              />
            )}
            <div className="page-content-box__body">{children}</div>
            {footerStyleEnabled && <FormFooter style={formState?.style} />}
          </div>
        </section>
        {/* Isolated: injected third-party scripts that patch HTMLElement.appendChild and
            JSON.stringify the appended node choke on the branding <a>, because React attaches a
            circular __reactFiber$* back-reference to it. Without its own boundary that throw
            reaches GlobalErrorBoundary and replaces the whole form (FORMS-WEB-X3, X4). */}
        {displayBranding && (
          <ErrorBoundary>
            <FooterBranding />
          </ErrorBoundary>
        )}
      </div>
    </ProgressBarSlotContext.Provider>
  );
};

export const PageContentBox = memo(PageContentBoxImpl);
